import type { FC, ReactNode } from 'react'
import type { ClinicSettings, Consultation, Patient, Prescription } from '@/lib/clinic/types'

import { cn } from '@/lib/utils'
import { formatDateTime } from '@/lib/clinic/datetime'
import { ageGenderLabel, formatPhone } from '@/lib/clinic/format'

export type PrintablePrescription = Pick<Prescription, 'number' | 'prescribedAt' | 'complaint' | 'diagnosis' | 'medicines' | 'advice'>

interface PrescriptionSheetProps {
    prescription: PrintablePrescription
    patient: Patient
    consultation: Consultation | null
    settings: ClinicSettings
    today: string
    draft?: boolean
}

interface DetailProps {
    label: string
    value: ReactNode
}

const Detail: FC<DetailProps> = ({ label, value }): ReactNode => {
    return (
        <div className='flex min-w-0 gap-[2mm]'>
            <span className='w-[27mm] shrink-0 text-[#6b7280]'>
                {label}
            </span>

            <span className='min-w-0 font-semibold break-words text-[#1f2a37]'>
                {value}
            </span>
        </div>
    )
}

interface SectionProps {
    label: string
    children: ReactNode
}

const Section: FC<SectionProps> = ({ label, children }): ReactNode => {
    return (
        <section className='mt-[4.5mm] break-inside-avoid'>
            <h3 className='text-[8.5pt] font-bold tracking-[0.14em] text-[#0f766e] uppercase'>
                {label}
            </h3>

            <div className='mt-[1.2mm] leading-[1.45]'>
                {children}
            </div>
        </section>
    )
}

const PrescriptionSheet: FC<PrescriptionSheetProps> = ({
    prescription,
    patient,
    consultation,
    settings,
    today,
    draft = false
}): ReactNode => {
    const fullHeader = settings.printHeader === 'full'
    const large = settings.printTextSize === 'large'
    const adviceLines = prescription.advice.split('\n').map(line => line.trim()).filter(Boolean)
    const showLogo = settings.showLogo && Boolean(settings.logo)

    return (
        <article
            className={cn(
                'rx-sheet relative flex min-h-[297mm] w-[210mm] flex-col bg-white px-[16mm] pb-[12mm] font-sans text-[#1f2a37]',
                large ? 'text-[11.5pt]' : 'text-[10pt]'
            )}
            style={{ paddingTop: fullHeader ? '14mm' : `${settings.letterheadSpace}mm` }}
        >
            {fullHeader && (
                <header className='flex items-start justify-between gap-[8mm] border-b-[0.7mm] border-[#0f766e] pb-[5mm]'>
                    <div className='flex min-w-0 items-center gap-[4mm]'>
                        {showLogo && (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img src={settings.logo ?? ''} alt='' className='size-[18mm] shrink-0 object-contain' />
                        )}

                        <div className='min-w-0'>
                            <p className='text-[19pt] leading-[1.1] font-extrabold tracking-[0.06em] text-[#0f766e] uppercase'>
                                {settings.clinicName}
                            </p>

                            <p className='mt-[1.5mm] text-[10.5pt] font-semibold text-[#1f2a37]'>
                                {settings.doctorName}
                            </p>

                            <p className='text-[9pt] text-[#4b5563]'>
                                {settings.qualification}
                            </p>
                        </div>
                    </div>

                    <div className='max-w-[64mm] shrink-0 text-right text-[8.5pt] leading-[1.5] text-[#4b5563]'>
                        <p className='whitespace-pre-line'>
                            {settings.address}
                        </p>

                        {settings.phone && (
                            <p className='mt-[1mm] font-semibold text-[#1f2a37]'>
                                Phone: {settings.phone}
                            </p>
                        )}
                    </div>
                </header>
            )}

            <div className={cn('flex items-end justify-between gap-[6mm]', fullHeader ? 'mt-[5mm]' : 'mt-0')}>
                <p className='text-[10pt] font-bold tracking-[0.22em] text-[#0f766e] uppercase'>
                    Prescription
                </p>

                <div className='text-right text-[9pt] leading-[1.5]'>
                    <p>
                        <span className='text-[#6b7280]'>Rx No.</span>{' '}
                        <span className='font-semibold'>{prescription.number}</span>
                    </p>

                    <p>
                        <span className='text-[#6b7280]'>Date</span>{' '}
                        <span className='font-semibold'>{formatDateTime(prescription.prescribedAt)}</span>
                    </p>
                </div>
            </div>

            <section className='mt-[3mm] grid grid-cols-2 gap-x-[8mm] gap-y-[1.6mm] rounded-[2mm] bg-[#f3f7f8] px-[5mm] py-[4mm] text-[9.5pt]'>
                <Detail label='Patient name' value={patient.name} />
                <Detail label='Patient ID' value={patient.code} />
                <Detail label='Age / Gender' value={ageGenderLabel(patient, today)} />
                <Detail label='Phone' value={formatPhone(patient.phone)} />
                <Detail label='Consultation' value={consultation ? formatDateTime(consultation.consultedAt) : 'Not linked'} />
                <Detail label='Place' value={patient.place || '—'} />
            </section>

            {settings.showComplaint && prescription.complaint && (
                <Section label='Chief complaint'>
                    {prescription.complaint}
                </Section>
            )}

            {prescription.diagnosis && (
                <Section label='Diagnosis'>
                    {prescription.diagnosis}
                </Section>
            )}

            <section className='mt-[5mm]'>
                <p className='font-serif text-[20pt] leading-none font-bold text-[#0f766e] italic'>
                    Rx
                </p>

                <table className='mt-[2.5mm] w-full border-collapse text-left text-[9.5pt]'>
                    <thead>
                        <tr className='bg-[#0f766e] text-[8.5pt] text-white'>
                            <th className='w-[8mm] px-[2mm] py-[1.8mm] text-center font-semibold'>#</th>
                            <th className='px-[2mm] py-[1.8mm] font-semibold'>Medicine</th>
                            <th className='w-[24mm] px-[2mm] py-[1.8mm] font-semibold'>Dosage</th>
                            <th className='w-[22mm] px-[2mm] py-[1.8mm] font-semibold'>Frequency</th>
                            <th className='w-[20mm] px-[2mm] py-[1.8mm] font-semibold'>Duration</th>
                            <th className='w-[42mm] px-[2mm] py-[1.8mm] font-semibold'>Instructions</th>
                        </tr>
                    </thead>

                    <tbody>
                        {prescription.medicines.length === 0 ? (
                            <tr className='border-b border-[#e5e7eb]'>
                                <td colSpan={6} className='px-[2mm] py-[3mm] text-center text-[#6b7280]'>
                                    No medicines prescribed.
                                </td>
                            </tr>
                        ) : (
                            prescription.medicines.map((medicine, index) => (
                                <tr key={medicine.id || index} className='border-b border-[#e5e7eb] align-top break-inside-avoid'>
                                    <td className='px-[2mm] py-[2.2mm] text-center text-[#6b7280]'>{index + 1}</td>
                                    <td className='px-[2mm] py-[2.2mm] font-semibold'>{medicine.name}</td>
                                    <td className='px-[2mm] py-[2.2mm]'>{medicine.dosage || '—'}</td>
                                    <td className='px-[2mm] py-[2.2mm] tabular-nums'>{medicine.frequency || '—'}</td>
                                    <td className='px-[2mm] py-[2.2mm]'>{medicine.duration || '—'}</td>
                                    <td className='px-[2mm] py-[2.2mm]'>{medicine.instructions || '—'}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                <p className='mt-[1.5mm] text-[7.5pt] text-[#6b7280]'>
                    Frequency: Morning - Afternoon - Night. SOS: only when needed.
                </p>
            </section>

            {adviceLines.length > 0 && (
                <Section label='Advice'>
                    <ul className='flex flex-col gap-[1mm]'>
                        {adviceLines.map((line, index) => (
                            <li key={index} className='flex gap-[2mm]'>
                                <span className='text-[#0f766e]' aria-hidden='true'>•</span>
                                <span>{line}</span>
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            <div className='mt-auto pt-[12mm]'>
                <div className='flex justify-end break-inside-avoid'>
                    <div className='w-[62mm] text-center'>
                        <div className='h-[14mm]' />

                        <div className='border-t border-[#9ca3af] pt-[1.5mm]'>
                            <p className='font-semibold'>
                                {settings.signatureName || settings.doctorName}
                            </p>

                            {settings.signatureDetails && (
                                <p className='text-[8.5pt] text-[#4b5563]'>
                                    {settings.signatureDetails}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {settings.footer && (
                    <p className='mt-[6mm] border-t border-[#e5e7eb] pt-[3mm] text-center text-[8pt] leading-[1.5] text-[#6b7280]'>
                        {settings.footer}
                    </p>
                )}
            </div>

            {draft && (
                <div className='pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden' aria-hidden='true'>
                    <span className='-rotate-[28deg] text-[64pt] font-black tracking-[0.2em] text-black/[0.045]'>
                        PREVIEW
                    </span>
                </div>
            )}
        </article>
    )
}

export default PrescriptionSheet
